/** @format */

export const PLANNING_PRESENCE_MAX_VISIBLE = 4;
export const PLANNING_FOCUS_CHIP_MAX_VISIBLE = 2;

export function userInitials(firstName, lastName) {
  const first = String(firstName || '').trim();
  const last = String(lastName || '').trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first.length >= 2) return first.slice(0, 2).toUpperCase();
  if (first) return first[0].toUpperCase();
  if (last) return last[0].toUpperCase();
  return '?';
}

export function presenceNameColor(name) {
  const text = String(name || '');
  if (!text) return '#6b7280';
  let hash = 0;
  /* eslint-disable no-bitwise */
  for (let i = 0; i < text.length; i += 1) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */
  return color;
}

export function presenceUsersFromApi(data) {
  const rows = Array.isArray(data?.users)
    ? data.users
    : Array.isArray(data)
    ? data
    : [];
  return rows
    .map((row) => {
      const firstName = row.first_name || row.firstName || '';
      const lastName = row.last_name || row.lastName || '';
      const fullName = (
        row.full_name ||
        row.fullName ||
        `${firstName} ${lastName}`
      ).trim();
      return {
        id: Number(row.id),
        firstName,
        lastName,
        fullName,
        hasAvatar: Boolean(row.has_avatar ?? row.hasAvatar),
        initials: row.initials || userInitials(firstName, lastName),
      };
    })
    .filter((row) => Number.isFinite(row.id) && row.id > 0);
}

function toPositiveInt(value) {
  if (value == null || value === '') return null;
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return null;
  return number;
}

export function planningFocusesFromApi(data) {
  const rows = Array.isArray(data?.focuses)
    ? data.focuses
    : Array.isArray(data)
    ? data
    : [];
  return rows
    .map((row) => {
      const userPayload = row?.user || row;
      const [user] = presenceUsersFromApi({ users: [userPayload] });
      if (!user) return null;
      return {
        user,
        chantierId: toPositiveInt(row.chantier_id ?? row.chantierId),
        tripId: toPositiveInt(row.trip_id ?? row.tripId),
        technicianId: toPositiveInt(row.technician_id ?? row.technicianId),
        layerId: toPositiveInt(row.layer_id ?? row.layerId),
        planId: toPositiveInt(row.plan_id ?? row.planId),
      };
    })
    .filter(Boolean);
}

export function remotePlanningFocuses(focuses, currentUserId) {
  return (focuses || []).filter(
    (row) => Number(row.user?.id) !== Number(currentUserId),
  );
}

export function focusesForProject(focuses, chantierId, currentUserId) {
  const id = toPositiveInt(chantierId);
  if (id == null) return [];
  return remotePlanningFocuses(focuses, currentUserId)
    .filter((row) => Number(row.chantierId) === id)
    .sort((left, right) => left.user.id - right.user.id);
}

export function focusesForProjectPaper(focuses, chantierId, currentUserId) {
  return focusesForProject(focuses, chantierId, currentUserId).filter(
    (row) => row.tripId == null && row.layerId == null,
  );
}

export function focusesForLayer(
  focuses,
  layerId,
  currentUserId,
  { expanded } = {},
) {
  const id = toPositiveInt(layerId);
  if (id == null) return [];
  return remotePlanningFocuses(focuses, currentUserId)
    .filter((row) => {
      if (Number(row.layerId) !== id) return false;
      if (expanded && row.tripId != null) return false;
      return true;
    })
    .sort((left, right) => left.user.id - right.user.id);
}

export function focusesForTripRow(focuses, tripId, currentUserId) {
  const id = toPositiveInt(tripId);
  if (id == null) return [];
  return remotePlanningFocuses(focuses, currentUserId)
    .filter((row) => Number(row.tripId) === id)
    .sort((left, right) => left.user.id - right.user.id);
}

export function tripBlockFocusTarget(plan) {
  if (!plan) return { tripId: null, planId: null };
  return {
    tripId:
      plan.tripId ??
      plan.interventionId ??
      plan.afterSalesVisitId ??
      plan.technicalVisitId ??
      null,
    planId: plan.id ?? null,
  };
}

export function focusesForTrip(focuses, tripId, technicianId, currentUserId) {
  return focusesForTripBlock(focuses, { tripId, technicianId }, currentUserId);
}

export function focusesForTripBlock(
  focuses,
  { tripId, planId } = {},
  currentUserId,
  { includeSelf = false } = {},
) {
  const trip = toPositiveInt(tripId);
  const plan = toPositiveInt(planId);
  const rows = includeSelf
    ? (focuses || []).filter((row) => Number.isFinite(Number(row.user?.id)))
    : remotePlanningFocuses(focuses, currentUserId);
  return rows
    .filter((row) => {
      if (plan != null && row.planId != null && Number(row.planId) === plan) {
        return true;
      }
      return trip != null && Number(row.tripId) === trip;
    })
    .sort((left, right) => left.user.id - right.user.id);
}

export function splitFocusAvatars(
  users,
  maxVisible = PLANNING_FOCUS_CHIP_MAX_VISIBLE,
) {
  const list = [...(users || [])].sort((left, right) => left.id - right.id);
  if (list.length <= maxVisible) return { visible: list, hidden: [] };
  return {
    visible: list.slice(0, maxVisible),
    hidden: list.slice(maxVisible),
  };
}

export function splitPresenceAvatars(
  users,
  currentUserId,
  maxVisible = PLANNING_PRESENCE_MAX_VISIBLE,
) {
  const list = users || [];
  const byName = (left, right) =>
    String(left.fullName || '').localeCompare(
      String(right.fullName || ''),
      'fr',
    );
  const me = list.filter((row) => Number(row.id) === Number(currentUserId));
  const others = list
    .filter((row) => Number(row.id) !== Number(currentUserId))
    .sort(byName);
  if (!me.length) {
    if (others.length <= maxVisible) return { visible: others, hidden: [] };
    return {
      visible: others.slice(0, maxVisible),
      hidden: others.slice(maxVisible),
    };
  }
  const roomForOthers = Math.max(0, maxVisible - 1);
  if (others.length <= roomForOthers) {
    return { visible: [...others, ...me], hidden: [] };
  }
  return {
    visible: [...others.slice(0, roomForOthers), ...me],
    hidden: others.slice(roomForOthers),
  };
}
